import { trackUserEvent, TrackUserEventPayload } from './senders';

/**
 * Reporting a completed purchase has to happen exactly once, and only in the
 * browser that actually did the buying.
 *
 * It used to be decided server-side by Purchase.is_fresh(): fire only if the
 * money had moved in the last 15 seconds. That held while every provider
 * created the MoneyTransaction in the request that then redirected the buyer
 * back to us, so the window had a single hop to cover. Stripe's hosted Checkout
 * added a second processor -- the checkout.session.completed webhook usually
 * settles the payment while the buyer is still on Stripe's page -- so the
 * window was spent on Stripe's redirect and our page load before they ever
 * arrived, and the sale went uncounted. Intermittently, which is why it was
 * reported as "some of them" (GATE-512).
 *
 * So the browser CLAIMS the purchase on its way out to the payment provider,
 * which is the moment it certainly knows a purchase is happening, and spends
 * that claim when the buyer lands back on a result page. A result URL that is
 * bookmarked, shared, or synced to another device carries no claim -- or a
 * spent one -- and reports nothing. That is the case "was this recent?" could
 * never tell apart from the happy path: it counted a bookmark opened within 15
 * seconds and missed a real buyer who took 16.
 */
const CLAIMS_KEY = 'gateme.purchase_claims';

/**
 * One entry per purchase this browser has paid for. Generous for any real
 * buyer, and it bounds what we leave behind in their storage.
 */
const CLAIMS_MAX = 20;

interface PurchaseClaim {
  id: string;
  reported: boolean;
}

function isClaim(value: unknown): value is PurchaseClaim {
  return (
    typeof value === 'object' && value !== null && typeof (value as PurchaseClaim).id === 'string'
  );
}

/**
 * The claim key, always as a string.
 *
 * The two sides disagree about type and always will: user2.js hands over
 * `data.purchase_id` straight from JSON, which is a NUMBER, while the result page
 * renders it into a template literal, which is a STRING. With neither coerced,
 * `findClaim`'s === never matched and isClaim() filtered the stored claim out on
 * read, so nothing was ever reported -- the exact bug this module exists to fix,
 * reintroduced the moment the key changed from the hashid (a string) to the id.
 *
 * Coerced here rather than at the call sites because there are four of them
 * across three languages, and the next one will get it wrong too.
 */
function claimKey(purchaseId: string | number): string {
  return String(purchaseId);
}

function readClaims(): PurchaseClaim[] {
  try {
    const stored: unknown = JSON.parse(window.localStorage.getItem(CLAIMS_KEY) || 'null');
    // Anything else under our key -- an older format, or another script -- is
    // treated as absent rather than trusted.
    return Array.isArray(stored) ? stored.filter(isClaim) : [];
  } catch (_e) {
    // Private mode, blocked site data, or unparseable JSON.
    return [];
  }
}

function writeClaims(claims: PurchaseClaim[]): void {
  try {
    window.localStorage.setItem(CLAIMS_KEY, JSON.stringify(claims.slice(-CLAIMS_MAX)));
  } catch (_e) {
    // Storage unavailable or full. Nothing to do here; both callers behave
    // correctly when a claim cannot be stored -- see trackPurchaseOnce.
  }
}

function findClaim(claims: PurchaseClaim[], key: string): PurchaseClaim | null {
  for (let i = 0; i < claims.length; i += 1) {
    if (claims[i].id === key) {
      return claims[i];
    }
  }
  return null;
}

/**
 * Record that this browser is the one paying for `purchaseId`. Called on
 * the way out to the payment provider, before the buyer leaves the page.
 */
export function claimPurchaseForTracking(purchaseId: string | number): void {
  if (!purchaseId) {
    return;
  }

  const key = claimKey(purchaseId);
  const claims = readClaims();
  if (findClaim(claims, key)) {
    // Already claimed -- the buyer came back and paid again after a declined
    // card, say. The existing claim stands: if it has been spent it stays
    // spent, so a second attempt on an already-reported purchase cannot reopen
    // it.
    return;
  }

  claims.push({ id: key, reported: false });
  writeClaims(claims);
}

/** Report a completed purchase this browser claimed, at most once. */
export function trackPurchaseOnce(
  purchaseId: string | number,
  extraData: TrackUserEventPayload = {},
): void {
  if (!purchaseId) {
    return;
  }

  const claims = readClaims();
  const claim = findClaim(claims, claimKey(purchaseId));

  if (!claim) {
    // This browser never paid for it: a bookmarked or shared result URL, or the
    // same URL opened on a second device. The browser that did buy has already
    // reported it.
    //
    // A buyer whose storage is unavailable also lands here, because their claim
    // could not be written on the way out either. That costs their event.
    // Inflating every organiser's revenue would cost more.
    return;
  }
  if (claim.reported) {
    return;
  }

  // Spent before the event goes out, not after: if the page is reloaded while
  // the beacons are still in flight, losing one event beats sending two. The
  // claim is this browser's own record and nothing else can spend it.
  claim.reported = true;
  writeClaims(claims);

  trackUserEvent('purchase', extraData);
}
